import React from "react";
import { VstDropdown, VstButton } from "vstui";
import MarkerUtils from "../../utils/marker-utils";
import Dispatcher from "../../dispatcher";
import HighlightStore from "../../stores/highlight-store";
import MarkerStore from "../../stores/marker-store";
import HighlightAction from "../../actions/highlight-action";
import HighlightUtils from "../../utils/highlight-utils";
import MarkdownContainer from "../reader/notebook/markdown-container";
import MarkdownButtonsList from "../reader/notebook/markdown-buttons-list";
import MarkdownHelper from "../../helpers/markdown";
import YouTube from "../../helpers/youtube";
import Isvg from "../../../vendor/react-inlinesvg";
import EditNoteContainer from "../reader/notebook/edit-note-container";
import {
  isEmojiMarker,
  getEmojiMatch
} from "../../components/reader/highlights/emoji-reactions/helpers";
import styled from "styled-components";
import { cloud } from "@vitalsource/vstui/colors";

const NoteContent = styled.div`
  padding-left: 10px;
  width: 80%;
  text-decoration: underline dashed ${cloud.getShade(4)};
`;

const EmojiNoteIcon = styled.div`
  width: 20%;
  font-size: 3em;
  font-family: Segoe UI Emoji, sans-serif;
  .note-text > .note > p {
    text-align: center;
  }
`;

const NoteButton = styled.button`
  display: flex;
  align-items: center;
`;
class CardContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editMode: false,
      editedNoteText: props.noteText || "",
      menuOpen: false
    };

    this.userInitialsRef = React.createRef();

    this.onClick = this.onClick.bind(this);
    this._deleteNote = this._deleteNote.bind(this);
    this._editMobileNote = this._editMobileNote.bind(this);
    this._forceUpdate = this._forceUpdate.bind(this);
    this._noteEditorClick = this._noteEditorClick.bind(this);
    this._startEditingNote = this._startEditingNote.bind(this);
    this._toggleDropdowns = this._toggleDropdowns.bind(this);
    this._onAddNoteButtonClick = this._onAddNoteButtonClick.bind(this);
    this._onCancelButtonClick = this._onCancelButtonClick.bind(this);
    this._onDeleteHighlightClick = this._onDeleteHighlightClick.bind(this);
    this._onHighlightClick = this._onHighlightClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onMarkdownButtonClick = this._onMarkdownButtonClick.bind(this);
    this._onMobileCancel = this._onMobileCancel.bind(this);
    this._onMobileNoteSave = this._onMobileNoteSave.bind(this);
    this._onMobileOpen = this._onMobileOpen.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onNoteChange = this._onNoteChange.bind(this);
    this._onSaveButtonClick = this._onSaveButtonClick.bind(this);
    this._onViewInBookClick = this._onViewInBookClick.bind(this);
  }

  componentDidMount() {
    window.addEventListener("click", this.onClick);
  }

  componentDidUpdate() {
    if (this.state.editMode) {
      window.onbeforeunload = function() {
        return i18n.t("reader.notebook.edit-on-before-unload");
      };
    } else {
      window.onbeforeunload = function() {};
    }
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.onClick);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Update the note text in the <textarea> if it's been updated elsewhere
    this.setState({
      editedNoteText: nextProps.noteText || ""
    });
  }

  onClick(event) {
    const { query, syncId } = this.props;
    const noteCardId = `notecard_${query ? "query_" : ""}${syncId}`;
    const nameClassFilter = ({ nodeName, className }) => {
      return nodeName === "DIV" && className === "note";
    };

    // https://gist.github.com/leofavre/d029cdda0338d878889ba73c88319295
    let path = event.composedPath ? event.composedPath() : event.path;
    if (!path) {
      const getParents = (node, memo = []) => {
        const parentNode = node.parentNode;

        if (!parentNode) {
          return memo;
        }

        return getParents(parentNode, memo.concat([parentNode]));
      };

      path = [event.target].concat(getParents(event.target));
    }

    if (
      !path.find(
        el => el.id === noteCardId || el.id === `markdown-${this.props.syncId}`
      )
    ) {
      this._saveNote();
      this._stopEditingNote();
    } else if (path.find(nameClassFilter)) {
      this._startEditingNote();
    }
  }

  // Private methods

  _goToHighlight() {
    HighlightUtils.goToHighlight(this.props.isbn, this.props);
    if (Fraction.Helpers.isSmallScreen()) {
      Dispatcher.emit(Dispatcher.HIDE_ALL_PANELS, "notebook");
    }
  }

  _deleteHighlight = () => {
    const { isbn, syncId } = this.props;
    const highlight = HighlightStore.get(isbn, syncId);
    HighlightAction.deleteHighlight(isbn, highlight.syncId);
    Fraction.Reporter.gaEvent("reader/panel/notebook/delete-highlight");
  };

  _deleteNote() {
    this.setState({ editedNoteText: "" }, this._saveNote);
    Fraction.Reporter.gaEvent("reader/panel/notebook/delete-note");
  }

  _startEditingNote() {
    const emojiNote = isEmojiMarker(MarkerStore.get(this.props.markerId));
    if (!this.props.subscribed && !emojiNote) {
      this.setState({ editMode: true });
    }
  }

  _stopEditingNote() {
    this.setState({ editMode: false });
  }

  _editMobileNote() {
    Fraction.Helpers.showModal({
      title: false,
      content: (
        <EditNoteContainer
          markerLabel={MarkerUtils.getMarkerLabel(this.props.markerId)}
          lastModifiedString={this.props.lastModifiedString}
          highlightColor={this.props.vstColor}
          highlightText={this.props.selectedText}
          noteText={this.props.noteText}
          openCallback={this._onMobileOpen}
          cancelCallback={this._onMobileCancel}
          saveCallback={this._onMobileNoteSave}
        />
      ),
      buttons: [],
      fullscreen: true,
      intersitial: true
    });
  }

  _onMobileOpen() {
    document.getElementById("panel-container").style.top = "100%";
  }

  _onMobileCancel() {
    document.getElementById("panel-container").style.top = 0;
    this._stopEditingNote();
  }

  _onMobileNoteSave(note) {
    document.getElementById("panel-container").style.top = 0;
    this.setState(
      {
        editedNoteText: note,
        editMode: false
      },
      this._saveNote
    );
  }

  // only saves the note if the text has changed
  _saveNote() {
    const { noteText, hasNote, isbn, syncId } = this.props;
    const editedNoteText = this.state.editedNoteText;
    if (editedNoteText !== noteText) {
      if (hasNote) {
        Fraction.Reporter.gaEvent("reader/panel/notebook/edit-note");
      } else {
        Fraction.Reporter.gaEvent("reader/panel/notebook/add-note");
      }

      HighlightAction.updateHighlight(isbn, syncId, {
        noteText: editedNoteText
      });
    }
  }

  // Event handlers

  _onMouseDown(e) {
    e.preventDefault();
  }

  _onNoteChange(e) {
    this.setState({
      editedNoteText: e.currentTarget.value
    });
  }

  _onKeyUp(e) {
    this._closeDropdowns();
    e.stopPropagation();
  }

  _onKeyDown(e) {
    const noteText = this._setNoteText();
    MarkdownHelper._list(e, this.note, noteText, this._onMarkdownButtonClick);
  }

  _onAddNoteButtonClick(e) {
    if (Fraction.Helpers.isSmallScreen()) {
      this._editMobileNote();
    } else {
      this._startEditingNote();
    }
  }

  _onSaveButtonClick(e) {
    if (this.state.editMode) {
      e.stopPropagation();
      this._stopEditingNote();
    }
    this._saveNote();
  }

  _onCancelButtonClick(e) {
    this._stopEditingNote();
    this.setState({
      editedNoteText: this.props.noteText
    });
  }

  _onViewInBookClick(e) {
    this._goToHighlight();
    Fraction.Reporter.gaEvent(
      "reader/panel/notebook/navigate-to-highlight",
      "via menu"
    );
  }

  _onDeleteHighlightClick(e) {
    if (this.props.hasNote) {
      Fraction.Helpers.showModal(
        {
          title: i18n.t("reader.notebook.note-associated-title"),
          content: (
            <span>{i18n.t("reader.notebook.note-associated-text")}</span>
          ),
          buttons: [
            {
              label: i18n.t("base.cancel"),
              click: Fraction.Helpers.removeModal,
              color: "gray"
            },
            {
              label: i18n.t("base.delete"),
              click: () => {
                Fraction.Helpers.removeModal();
                this._deleteHighlight();
              }
            }
          ]
        },
        e.currentTarget
      );
    } else {
      this._deleteHighlight();
    }
  }

  _onHighlightClick(e) {
    this._goToHighlight();
    Fraction.Reporter.gaEvent(
      "reader/panel/notebook/navigate-to-highlight",
      "via highlight"
    );
  }

  _onMarkdownButtonClick(noteText, start, end) {
    if (this.state.editMode) {
      this.setState({ editedNoteText: noteText }, () => {
        this.note.selectionStart = start;
        this.note.selectionEnd = end;
        this.note.focus();
      });
    }
  }

  _forceUpdate() {
    // Allows markdown buttons list to keep track of refs
    this.forceUpdate();
  }

  _setNoteText() {
    const noteText = this.state.editedNoteText;
    if (this.props.query) {
      const re = new RegExp(`(${this.props.query})`, "ig");
      if (noteText) {
        const html =
          "<span style='background:#4A4A58;color:#FFFFFF;padding-left:2px;padding-right:3px;'>$1</span>";
        return window.toHTML(
          Fraction.Helpers.stripTags(noteText).replace(re, html)
        );
      }
    }
    return noteText;
  }

  _setSelectedText() {
    if (this.props.query) {
      const re = new RegExp(`(${this.props.query})`, "ig");
      const html =
        "<span style='background:#4A4A58;color:#FFFFFF;padding-left:2px;padding-right:3px;'>$1</span>";
      return window.toHTML(
        Fraction.Helpers.stripTags(this.props.selectedText).replace(re, html)
      );
    }
    return this.props.selectedText;
  }

  _noteEditorClick(e) {
    this._closeDropdowns();
    this.state.editMode && e.stopPropagation();
  }

  _closeDropdowns() {
    this.setState({ dropdownOpen: false });
  }

  _toggleDropdowns() {
    this.setState(prevState => ({ dropdownOpen: !prevState.dropdownOpen }));
  }

  renderEditMode() {
    const noteText = this._setNoteText();
    return (
      <div>
        <MarkdownButtonsList
          className="markdown-buttons"
          onChange={this._onMarkdownButtonClick}
          dropdownOpen={this.state.dropdownOpen}
          toggleDropdown={this._toggleDropdowns}
          selectionStart={this.note && this.note.selectionStart}
          selectionEnd={this.note && this.note.selectionEnd}
          note={this.state.editedNoteText}
        />
        <textarea
          rows="1"
          aria-label={
            noteText
              ? i18n.t("reader.notebook.edit-note")
              : i18n.t("reader.notebook.add-note")
          }
          autoFocus={true}
          className="note full"
          id="add-note-textarea"
          placeholder={i18n.t("reader.notebook.add-note")}
          value={noteText}
          readOnly={this.props.subscribed}
          ref={note => {
            this.note = note;
          }}
          onFocus={this._startEditingNote}
          onChange={this._onNoteChange}
          onClick={this._forceUpdate}
          onKeyUp={this._onKeyUp}
          onKeyDown={this._onKeyDown}
        />
        <div
          className={
            this.state.editMode && !this.props.subscribed
              ? "edit-buttons"
              : "edit-buttons hidden"
          }
        >
          <button
            className="cancel-button"
            onMouseDown={this._onMouseDown}
            onClick={this._onCancelButtonClick}
          >
            {i18n.t("base.cancel")}
          </button>
          <button
            className="save-button"
            onMouseDown={this._onMouseDown}
            onClick={this._onSaveButtonClick}
          >
            {i18n.t("base.submit")}
          </button>
        </div>
      </div>
    );
  }

  renderMobileMarkdown() {
    let noteText = this._setNoteText();
    const emojiNote = isEmojiMarker(MarkerStore.get(this.props.markerId));
    if (emojiNote && getEmojiMatch(this._setNoteText())) {
      noteText = getEmojiMatch(this._setNoteText())[0];
    }
    if (this.props.subscribed) {
      return (
        <MarkdownContainer
          ariaLabel={
            noteText
              ? i18n.t("reader.notebook.edit-note")
              : i18n.t("reader.notebook.add-note")
          }
          className="note"
          markdown={noteText}
          placeholder={i18n.t("reader.notebook.add-note")}
        />
      );
    } else {
      return (
        <button className="note" onClick={!emojiNote && this._editMobileNote}>
          {!emojiNote && <Isvg src="img/note.svg" />}
          <MarkdownContainer
            ariaLabel={
              noteText
                ? i18n.t("reader.notebook.edit-note")
                : i18n.t("reader.notebook.add-note")
            }
            className="note"
            markdown={noteText}
            onClick={!emojiNote && this._startEditingNote}
            placeholder={i18n.t("reader.notebook.add-note")}
          />
        </button>
      );
    }
  }

  renderNote() {
    if (Fraction.Helpers.isSmallScreen()) {
      return this.renderMobileMarkdown();
    } else {
      const emojiNote = isEmojiMarker(MarkerStore.get(this.props.markerId));
      let noteText = this._setNoteText();
      if (emojiNote && getEmojiMatch(this._setNoteText())) {
        noteText = getEmojiMatch(this._setNoteText())[0];
      }
      return (
        <div className={this.state.editMode ? "" : "note-text"}>
          {!emojiNote && <Isvg src="img/small_note.svg" />}
          <MarkdownContainer
            ariaLabel={
              noteText
                ? i18n.t("reader.notebook.edit-note")
                : i18n.t("reader.notebook.add-note")
            }
            className="note"
            markdown={noteText}
            onClick={this._startEditingNote}
            tabIndex={0}
            placeholder={i18n.t("reader.notebook.add-note")}
          />
        </div>
      );
    }
  }

  renderYoutubeFrame() {
    if (this.props.noteText) {
      return (
        <div id="youTubeFrame">
          {YouTube._renderYouTubeVideo(this.props.noteText)}
        </div>
      );
    }
  }

  renderNoteSection() {
    const noteText = this._setNoteText();
    if (this.props.query || (this.props.hasNote && this.props.subscribed)) {
      return (
        <div className="note-editor">
          <div className="note">
            {this.renderNote()}
            {this.renderYoutubeFrame()}
          </div>
        </div>
      );
    } else {
      if (this.props.hasNote || this.state.editMode) {
        return (
          <div className="note-editor" onClick={this._noteEditorClick}>
            <label htmlFor="add-note-textarea">
              {noteText
                ? i18n.t("reader.notebook.edit-note")
                : i18n.t("reader.notebook.add-note")}
            </label>
            {this.state.editMode ? this.renderEditMode() : this.renderNote()}
            {this.renderYoutubeFrame()}
          </div>
        );
      } else {
        if (!this.props.subscribed) {
          return (
            <button
              className="add-note-button"
              onClick={this._onAddNoteButtonClick}
            >
              {i18n.t("reader.notebook.add-note")}
            </button>
          );
        }
      }
    }
  }

  renderOptions() {
    const emojiNote = isEmojiMarker(MarkerStore.get(this.props.markerId));
    const target = (
      <button className="vst-dropdown-target noButton">
        <Isvg
          src="/img/3-dot_menu.svg"
          className={this.state.menuOpen ? "open" : ""}
        />
      </button>
    );
    return (
      <VstDropdown
        vertical="bottom"
        horizontal="right"
        target={target}
        onOpen={() => this.setState({ menuOpen: true })}
        onClose={() => this.setState({ menuOpen: false })}
      >
        <VstDropdown.Item onClick={this._onViewInBookClick}>
          {i18n.t("reader.notebook.view-in-book")}
        </VstDropdown.Item>

        <VstDropdown.Item
          hidden={emojiNote || this.props.subscribed || this.props.query}
          onClick={this._onDeleteHighlightClick}
        >
          {i18n.t("reader.notebook.delete-highlight")}
        </VstDropdown.Item>

        <VstDropdown.Item
          hidden={!this.props.hasNote || this.props.subscribed}
          onClick={emojiNote ? this._deleteHighlight : this._deleteNote}
        >
          {i18n.t("reader.notebook.delete-note")}
        </VstDropdown.Item>

        <VstDropdown.Item hidden={!this.props.query}>
          {i18n.t("reader.notebook.edit-in-notebook")}
        </VstDropdown.Item>
      </VstDropdown>
    );
  }

  renderHeader() {
    return (
      <div className="notecard-header">
        <div className="left">
          <div className="last-modified">{this.props.lastModifiedString}</div>
        </div>

        <div className="right">{this.renderOptions()}</div>
      </div>
    );
  }

  renderInfo(emojiNote) {
    const {
      subscribed,
      userInitials,
      authorName,
      vstColor,
      markerId
    } = this.props;
    const markerLabel = MarkerUtils.getMarkerLabel(markerId);
    const hasAuthor = subscribed && userInitials && authorName;

    const author = hasAuthor && (
      <div className="marker-author" style={{ backgroundColor: vstColor }}>
        <div className="user-initials" title={authorName} ref={this.userInitialsRef}>
          {userInitials}
        </div>
      </div>
    );

    const label = markerLabel && (
      <div className="marker-name" title={markerLabel}>
        {markerLabel}
      </div>
    );

    return (
      <div className="marker-info">
        {author}
        {!emojiNote && label}
      </div>
    );
  }

  renderEmojiNote(isEmojiNote) {
    const { subscribed, query, syncId } = this.props;
    const parsedHighlight = getEmojiMatch(this._setSelectedText());
    let highlightedText = this._setSelectedText();

    if (isEmojiNote && parsedHighlight && parsedHighlight[0]) {
      highlightedText = parsedHighlight.input
        .split(parsedHighlight[0])
        .join("");
    }
    return (
      <li
        id={`notecard_${query ? "query" : ""}${syncId}`}
        className={subscribed ? "notecard subscribed" : "notecard"}
      >
        {this.renderHeader()}
        {this.renderInfo(isEmojiNote)}
        <NoteButton className="highlight" onClick={this._onHighlightClick}>
          <EmojiNoteIcon>{this.renderNote()}</EmojiNoteIcon>
          <NoteContent>{highlightedText}</NoteContent>
        </NoteButton>
      </li>
    );
  }

  renderNormalNote() {
    const { subscribed, query, syncId, vstColorDark, vstColor } = this.props;
    return (
      <li
        id={`notecard_${query ? "query" : ""}${syncId}`}
        className={subscribed ? "notecard subscribed" : "notecard"}
        onClick={this._onSaveButtonClick}
      >
        {this.renderHeader()}
        {this.renderInfo()}

        <button className="highlight" onClick={this._onHighlightClick}>
          <div
            className="highlight-text"
            style={
              subscribed
                ? { borderColor: vstColorDark }
                : { background: vstColor }
            }
          >
            {this._setSelectedText()}
          </div>
        </button>

        {this.renderNoteSection()}
      </li>
    );
  }

  render() {
    const isEmojiNote = isEmojiMarker(MarkerStore.get(this.props.markerId));
    if (isEmojiNote) {
      return this.renderEmojiNote(isEmojiNote);
    } else {
      return this.renderNormalNote();
    }
  }
}

CardContainer.defaultProps = {
  noteText: ""
};

export default CardContainer;
